import type { scaleLinear } from 'd3';

import { LineChartAxisSide, type LineChartAxis } from './ResponsiveLineChart.types';
import { axisWidth, compactNumber } from './ResponsiveLineChart.utils';

const axisLabelMaxRowCharacters = 30;
const axisLabelRowGap = 12;

export function ChartAxis({
  axis,
  index,
  innerHeight,
  innerWidth,
  scale,
}: {
  axis: LineChartAxis;
  index: number;
  innerHeight: number;
  innerWidth: number;
  scale: ReturnType<typeof scaleLinear>;
}) {
  const left = axis.side === LineChartAxisSide.Left;
  const x = left ? -12 - index * axisWidth : innerWidth + 12 + index * axisWidth;
  const labelX = x + (left ? -42 : 42);
  const labels = axis.labels?.length ? axis.labels : [{ label: axis.label, color: axis.color }];
  const labelRows = chartAxisLabelRows(labels);
  const formatter = axis.axisValueFormatter ?? axis.valueFormatter ?? compactNumber;
  return (
    <g data-axis-id={axis.id}>
      {labelRows.map((row, rowIndex) => {
        const rowX = labelX + (rowIndex - (labelRows.length - 1) / 2) * axisLabelRowGap;
        return (
          <text
            key={`${axis.id}-${String(rowIndex)}`}
            data-chart-axis-label-row
            x={rowX}
            y={innerHeight / 2}
            transform={`rotate(${left ? '-90' : '90'} ${String(rowX)} ${String(innerHeight / 2)})`}
            fill="var(--muted-foreground)"
            className="text-[10px] font-semibold"
            textAnchor="middle"
          >
            {row.map((label, labelIndex) => (
              <tspan key={`${label.label}-${String(labelIndex)}`} fill={label.color}>
                {labelIndex > 0 ? ' & ' : ''}
                {label.label}
              </tspan>
            ))}
          </text>
        );
      })}
      {scale.ticks(4).map((tick) => (
        <text
          key={tick}
          x={x}
          y={Number(scale(tick))}
          dy="0.32em"
          fill={axis.color}
          className="text-[10px]"
          textAnchor={left ? 'end' : 'start'}
        >
          {formatter(tick)}
        </text>
      ))}
    </g>
  );
}

function chartAxisLabelRows(labels: Array<{ label: string; color: string }>) {
  return labels.reduce<Array<Array<{ label: string; color: string }>>>((rows, label) => {
    const currentRow = rows.at(-1);
    const currentLength =
      currentRow?.reduce(
        (length, item, index) => length + item.label.length + (index > 0 ? 3 : 0),
        0,
      ) ?? 0;
    const nextLength = currentLength + label.label.length + (currentRow?.length ? 3 : 0);
    if (!currentRow || (currentRow.length > 0 && nextLength > axisLabelMaxRowCharacters)) {
      rows.push([label]);
    } else {
      currentRow.push(label);
    }
    return rows;
  }, []);
}
